import React, { useState } from 'react';
import { Badge } from '@ygtechnologies/rubus-ui';
import { SearchInput } from '@ygtechnologies/bygr-frontend-component-library';
import { SortDirection } from '@/types';
import { getProjects } from '@/services/projects';
import { getProfiles } from '@/services/profiles';
import { useAsyncStateWithIsLoading } from '@/utils/hooks';
import theme from './EmailTemplates.module.scss';

export type ScopeKind = 'project' | 'profile';

export interface ScopeSelection {
    kind: ScopeKind;
    id: number;
    name: string;
    /** For projects: the seller profile that owns it, so choosing a project can fix the profile too. */
    ownerProfileId?: number;
    ownerProfileName?: string;
}

interface ScopePickerProps {
    kind: ScopeKind;
    selectedId: number | null;
    onChange(selection: ScopeSelection): void;
    /** Ids that already have an override, marked so existing work is not missed. */
    overriddenIds?: Set<number>;
    /** Projects only: narrows the list to this seller profile's projects. */
    filterProfileId?: number | null;
}

/**
 * Searchable project / seller-profile list. Both endpoints search server-side, so this asks for a small
 * page and re-queries as the text changes.
 */
const ScopePicker: React.FC<ScopePickerProps> = ({
    kind,
    selectedId,
    onChange,
    overriddenIds,
    filterProfileId,
}) => {
    const [searchText, setSearchText] = useState('');

    const [options, , , isLoading] = useAsyncStateWithIsLoading<ScopeSelection[]>(
        async () => {
            const req = {
                pageNumber: 0,
                pageSize: 20,
                searchText,
                sortBy: 'name',
                sortDirection: SortDirection.Ascending,
            };

            if (kind === 'project') {
                const result = await getProjects(req, filterProfileId);
                return result.items.map((x) => ({
                    kind: 'project' as const,
                    id: x.id,
                    name: x.name,
                    ownerProfileId: x.sellerProfile?.id,
                    ownerProfileName: x.sellerProfile?.name,
                }));
            }

            const result = await getProfiles(req);
            return result.items.map((x) => ({ kind: 'profile' as const, id: x.id, name: x.name }));
        },
        [kind, searchText, filterProfileId],
        []
    );

    return (
        <div className={theme.scopePicker}>
            <SearchInput searchText={searchText} onSearchTextChange={setSearchText} />
            <div className={theme.scopeList}>
                {isLoading && options.length === 0 && <div className={theme.scopeEmpty}>Loading…</div>}
                {!isLoading && options.length === 0 && (
                    <div className={theme.scopeEmpty}>No {kind === 'project' ? 'projects' : 'profiles'} found</div>
                )}
                {options.map((option) => (
                    <button
                        type="button"
                        key={option.id}
                        className={option.id === selectedId ? theme.scopeOptionSelected : theme.scopeOption}
                        onClick={() => onChange(option)}>
                        <span className={theme.scopeOptionName}>
                            {option.name}
                            {/* Redundant once the list is already narrowed to one profile. */}
                            {option.ownerProfileName && !filterProfileId && (
                                <span className={theme.scopeOptionMeta}>{option.ownerProfileName}</span>
                            )}
                        </span>
                        {overriddenIds?.has(option.id) && (
                            <Badge variant="info" appearance="subtle" size="xs">
                                override
                            </Badge>
                        )}
                    </button>
                ))}
            </div>
        </div>
    );
};

export default ScopePicker;
